<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    persistent
    max-width="480"
  >
    <v-card rounded="lg">
      <v-card-title
        class="text-subtitle-1 font-weight-bold d-flex align-center"
      >
        <v-icon start color="#c1121f">mdi-shield-key-outline</v-icon>
        {{ titulo }}
      </v-card-title>
      <v-divider />

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ error }}
        </v-alert>

        <!-- ===== SETUP: escanear QR + confirmar codigo ===== -->
        <div v-if="paso === 'qr'">
          <p class="text-body-2 mb-3">
            Escanea este código con tu app autenticadora (Google
            Authenticator, Microsoft Authenticator, Authy…).
          </p>

          <div class="d-flex justify-center mb-3" style="min-height: 200px">
            <v-progress-circular
              v-if="loading && !qrDataUrl"
              indeterminate
              color="#c1121f"
            />
            <img
              v-else-if="qrDataUrl"
              :src="qrDataUrl"
              alt="QR MFA"
              style="width: 200px; height: 200px"
            />
          </div>

          <div v-if="secret" class="text-center mb-3">
            <div class="text-caption text-medium-emphasis">
              ¿No puedes escanear? Ingresa esta clave manualmente:
            </div>
            <code class="text-body-2">{{ secret }}</code>
          </div>

          <v-text-field
            v-model="code"
            label="Código de 6 dígitos"
            variant="outlined"
            density="compact"
            hide-details
            maxlength="6"
            inputmode="numeric"
            class="mb-2"
            @keyup.enter="confirmar"
          />
          <v-btn
            block
            color="#c1121f"
            :loading="loading"
            :disabled="code.length !== 6"
            @click="confirmar"
          >
            Confirmar
          </v-btn>
        </div>

        <!-- ===== SETUP: recovery codes (una sola vez) ===== -->
        <div v-else-if="paso === 'recovery'">
          <v-alert type="warning" variant="tonal" density="compact" class="mb-3">
            Guarda estos códigos de recuperación. Se muestran
            <b>una sola vez</b> y te permiten entrar si pierdes tu teléfono.
          </v-alert>

          <v-sheet border rounded class="pa-3 mb-3">
            <div
              v-for="(rc, i) in recoveryCodes"
              :key="i"
              class="text-body-2 font-weight-medium"
            >
              {{ rc }}
            </div>
          </v-sheet>

          <div class="d-flex ga-2 mb-3">
            <v-btn
              variant="tonal"
              size="small"
              prepend-icon="mdi-content-copy"
              @click="copiarRecovery"
            >
              Copiar
            </v-btn>
            <v-btn
              variant="tonal"
              size="small"
              prepend-icon="mdi-download"
              @click="descargarRecovery"
            >
              Descargar
            </v-btn>
          </div>

          <v-checkbox
            v-model="guardados"
            label="Ya guardé mis códigos de recuperación"
            hide-details
            density="compact"
            class="mb-2"
          />
          <v-btn
            block
            color="#c1121f"
            :disabled="!guardados"
            @click="finalizarSetup"
          >
            Continuar
          </v-btn>
        </div>

        <!-- ===== VERIFY: ya enrolado ===== -->
        <div v-else-if="paso === 'verify'">
          <template v-if="!usarRecovery">
            <p class="text-body-2 mb-3">
              Ingresa el código de 6 dígitos de tu app autenticadora.
            </p>
            <v-text-field
              v-model="code"
              label="Código de 6 dígitos"
              variant="outlined"
              density="compact"
              hide-details
              maxlength="6"
              inputmode="numeric"
              class="mb-2"
              @keyup.enter="verificar"
            />
            <v-btn
              block
              color="#c1121f"
              :loading="loading"
              :disabled="code.length !== 6"
              @click="verificar"
            >
              Verificar
            </v-btn>
            <div class="text-center mt-2">
              <a
                href="#"
                class="text-caption"
                @click.prevent="usarRecovery = true"
              >
                Usar código de recuperación
              </a>
            </div>
          </template>

          <template v-else>
            <p class="text-body-2 mb-3">
              Ingresa uno de tus códigos de recuperación.
            </p>
            <v-text-field
              v-model="recoveryCode"
              label="Código de recuperación"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-2"
              @keyup.enter="verificar"
            />
            <v-btn
              block
              color="#c1121f"
              :loading="loading"
              :disabled="!recoveryCode.trim()"
              @click="verificar"
            >
              Verificar
            </v-btn>
            <div class="text-center mt-2">
              <a
                href="#"
                class="text-caption"
                @click.prevent="usarRecovery = false"
              >
                Usar código de la app
              </a>
            </div>
          </template>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="cerrar">Cancelar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QRCode from "qrcode";
import { mfaSetup, mfaConfirm, mfaVerify } from "@/api/mfa.js";

export default {
  name: "MfaDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    // "setup" (primera vez, enrola) | "verify" (ya enrolado)
    mode: { type: String, default: "verify" },
    mfaToken: { type: String, default: "" },
  },
  emits: ["update:modelValue", "verified"],
  data() {
    return {
      paso: "verify", // "qr" | "recovery" | "verify"
      loading: false,
      error: "",
      qrDataUrl: "",
      secret: "",
      code: "",
      recoveryCodes: [],
      guardados: false,
      usarRecovery: false,
      recoveryCode: "",
    };
  },
  computed: {
    titulo() {
      if (this.paso === "recovery") return "Códigos de recuperación";
      if (this.mode === "setup")
        return "Configurar verificación en dos pasos";
      return "Verificación en dos pasos";
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.iniciar();
    },
  },
  methods: {
    iniciar() {
      this.error = "";
      this.code = "";
      this.recoveryCode = "";
      this.usarRecovery = false;
      this.guardados = false;
      this.qrDataUrl = "";
      this.secret = "";
      if (this.mode === "setup") {
        this.paso = "qr";
        this.cargarQr();
      } else {
        this.paso = "verify";
      }
    },
    async cargarQr() {
      this.loading = true;
      try {
        const data = await mfaSetup(this.mfaToken);
        this.secret = data.secret || "";
        this.qrDataUrl = data.otpauth_uri
          ? await QRCode.toDataURL(data.otpauth_uri)
          : "";
      } catch (e) {
        this.error = this.msgError(
          e,
          "No se pudo iniciar la configuración MFA.",
        );
      } finally {
        this.loading = false;
      }
    },
    async confirmar() {
      this.loading = true;
      this.error = "";
      try {
        const data = await mfaConfirm(this.mfaToken, this.code);
        this.recoveryCodes = data.recovery_codes || [];
        this.paso = "recovery";
      } catch (e) {
        this.error = this.msgError(e, "Código inválido. Intenta de nuevo.");
      } finally {
        this.loading = false;
      }
    },
    finalizarSetup() {
      // Se asume que confirm ya dejo la sesion (cookies). El padre continua.
      this.$emit("verified", { fromSetup: true });
      this.$emit("update:modelValue", false);
    },
    async verificar() {
      this.loading = true;
      this.error = "";
      try {
        const data = await mfaVerify(
          this.mfaToken,
          this.usarRecovery
            ? { recoveryCode: this.recoveryCode.trim() }
            : { code: this.code },
        );
        this.$emit("verified", data || {});
        this.$emit("update:modelValue", false);
      } catch (e) {
        this.error = this.msgError(e, "Código incorrecto. Intenta de nuevo.");
      } finally {
        this.loading = false;
      }
    },
    copiarRecovery() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.recoveryCodes.join("\n"));
      }
    },
    descargarRecovery() {
      const blob = new Blob([this.recoveryCodes.join("\n")], {
        type: "text/plain",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "recovery-codes.txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    cerrar() {
      this.$emit("update:modelValue", false);
    },
    msgError(e, fallback) {
      return e?.response?.data?.detail || e?.response?.data?.error || fallback;
    },
  },
};
</script>
